import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Divider,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    Tooltip,
    Typography
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { diffWordsWithSpace } from 'diff';
import { AuthContext } from '../../../provider/AuthProvider';

const ADMIN_ONLY_ACTION_HINT = 'Nur Administratoren können Verbesserungsvorschläge erzeugen, annehmen oder ablehnen.';

const statusLabels = {
    open: 'Offen',
    accepted: 'Angenommen',
    rejected: 'Abgelehnt'
};

const typeLabels = {
    false_positive_rule: 'Falsch angewandt',
    missed_void_clause: 'Nicht erkannt'
};

const actionLabels = {
    delete: 'Löschen',
    update: 'Aktualisieren',
    split: 'Aufteilen',
    create: 'Neu erstellen'
};

const actionColors = {
    delete: 'error',
    update: 'warning',
    split: 'warning',
    create: 'success'
};

const outcomeLabels = {
    incorrect: 'Fehlerbeispiel',
    correct: 'Vergleichsbeispiel'
};

const evidenceTypeLabels = {
    false_application: 'Falsche Anwendung',
    correct_application: 'Korrekte Anwendung',
    correct_non_application: 'Korrekte Nicht-Anwendung',
    unclear_non_application: 'Nicht angewandt / unklar',
    default: 'Evidenz'
};

const evidenceTypeDescriptions = {
    false_application: 'Die KI hat diese Prüfanleitung als Grund für unwirksam genutzt, aber ein Mensch hat die Bewertung korrigiert.',
    correct_application: 'Diese Klausel zeigt, wann die Prüfanleitung tatsächlich greifen soll.',
    correct_non_application: 'Diese ähnliche Klausel zeigt, wo die Prüfanleitung zu Recht nicht angewandt wurde.',
    unclear_non_application: 'Diese ähnliche Klausel wurde nicht mit dieser Prüfanleitung bewertet, die Gesamtbewertung wurde aber korrigiert.',
    default: 'Beleg für diesen Verbesserungsvorschlag.'
};

const evidenceTypeColors = {
    false_application: 'error',
    correct_application: 'success',
    correct_non_application: 'success',
    unclear_non_application: 'warning',
    default: 'default'
};

const assessmentLabels = {
    valid: 'Gültig',
    unfair: 'Unfair',
    void: 'Unwirksam'
};

const simulationOutcomeLabels = {
    improved: 'Verbessert',
    worsened: 'Verschlechtert',
    unchanged: 'Unverändert'
};

const simulationOutcomeColors = {
    improved: 'success',
    worsened: 'error',
    unchanged: 'default'
};

const getNewestVersionText = (rule) => {
    if (!rule?.versions?.length) return '';
    return rule.versions[rule.versions.length - 1]?.text || '';
};

const getRuleTitle = (rule) => rule?.title || 'Keine Prüfanleitung';

const getProposalTitle = (proposal) => {
    if (proposal.action === 'delete') {
        return `Prüfanleitung löschen: ${getRuleTitle(proposal.rule)}`;
    }

    return proposal.title || getRuleTitle(proposal.rule);
};

const getAssessmentLabel = (assessment) => assessmentLabels[assessment] || assessment || '-';
const getActionColor = (action) => actionColors[action] || 'default';
const getSimulationColor = (outcome) => simulationOutcomeColors[outcome] || 'default';
const getChipBorderColor = (color) => color === 'default' ? 'grey.300' : `${color}.main`;
const getId = (value) => typeof value === 'object' ? value?._id : value;

const getHurtRuleIds = (aiReview) => (aiReview?.hurt_rules || []).map(getId);

const getEvidenceType = (suggestion, evidence) => {
    if (suggestion.type !== 'false_positive_rule') {
        return evidence.outcome === 'incorrect' ? 'false_application' : 'correct_application';
    }

    const suggestionRuleId = getId(suggestion.rule);
    const hurtRuleIds = getHurtRuleIds(evidence.aiReview);
    const ruleWasApplied = hurtRuleIds.includes(suggestionRuleId);

    if (!ruleWasApplied) {
        return evidence.outcome === 'incorrect' && evidence.humanReview?.assessment === 'void'
            ? 'unclear_non_application'
            : 'correct_non_application';
    }

    if (
        evidence.outcome === 'incorrect' &&
        evidence.aiReview?.assessment === 'void' &&
        evidence.humanReview &&
        evidence.humanReview.assessment !== evidence.aiReview.assessment
    ) {
        return 'false_application';
    }

    if (
        evidence.outcome === 'correct' &&
        evidence.aiReview?.assessment === 'void'
    ) {
        return 'correct_application';
    }

    return 'default';
};

const RuleImprovementSuggestionsTab = () => {
    const { user } = useContext(AuthContext);
    const canManageSuggestions = user?.role === 'admin';
    const [suggestions, setSuggestions] = useState([]);
    const [selectedSuggestionId, setSelectedSuggestionId] = useState(null);
    const [statusFilter, setStatusFilter] = useState('open');
    const [typeFilter, setTypeFilter] = useState('all');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchSuggestions = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await axios.get('/analytics/rule-improvement-suggestions');
            setSuggestions(response.data || []);
        } catch (fetchError) {
            console.error(fetchError);
            setError('Verbesserungsvorschläge konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuggestions();
    }, []);

    const filteredSuggestions = useMemo(() => {
        return suggestions.filter((suggestion) => {
            const statusMatches = statusFilter === 'all' || suggestion.status === statusFilter;
            const typeMatches = typeFilter === 'all' || suggestion.type === typeFilter;
            return statusMatches && typeMatches;
        });
    }, [suggestions, statusFilter, typeFilter]);

    useEffect(() => {
        if (filteredSuggestions.length === 0) {
            setSelectedSuggestionId(null);
            return;
        }

        if (!filteredSuggestions.some(suggestion => suggestion._id === selectedSuggestionId)) {
            setSelectedSuggestionId(filteredSuggestions[0]._id);
        }
    }, [filteredSuggestions, selectedSuggestionId]);

    const selectedSuggestion = filteredSuggestions.find(suggestion => suggestion._id === selectedSuggestionId);

    const handleGenerate = async () => {
        if (!canManageSuggestions) return;

        setError('');

        try {
            await axios.post('/analytics/rule-improvement-suggestions/generate');
            setSuggestions([]);
            setSelectedSuggestionId(null);
        } catch (generateError) {
            console.error(generateError);
            setError('Manuelle Analyse konnte nicht gestartet werden.');
        }
    };

    const handleDecision = async (suggestionId, decision) => {
        if (!canManageSuggestions) return;

        setError('');

        try {
            await axios.patch(`/analytics/rule-improvement-suggestions/${suggestionId}/${decision}`);
            await fetchSuggestions();
        } catch (decisionError) {
            console.error(decisionError);
            setError('Der Vorschlag konnte nicht aktualisiert werden.');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">Verbesserungsvorschläge</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                        Gespeicherte Vorschläge zur Verbesserung von Prüfanleitungen, basierend auf KI-Fehlern und Anwaltskorrekturen.
                        Vorschläge, deren Simulation mehr Verschlechterungen als Verbesserungen zeigt, werden bei der nächtlichen Analyse automatisch gelöscht und neu berechnet.
                    </Typography>
                </Box>
                <Tooltip title={canManageSuggestions ? '' : ADMIN_ONLY_ACTION_HINT}>
                    <span>
                        <Button
                            variant="contained"
                            startIcon={<AutoFixHighIcon />}
                            onClick={handleGenerate}
                            disabled={!canManageSuggestions}
                            sx={{ whiteSpace: 'nowrap', minWidth: 170, px: 2.5 }}
                        >
                            Neu analysieren
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 3 }}>
                {['open', 'accepted', 'rejected', 'all'].map(status => (
                    <Chip
                        key={status}
                        label={status === 'all' ? 'Alle Status' : statusLabels[status]}
                        color={statusFilter === status ? 'primary' : 'default'}
                        variant={statusFilter === status ? 'filled' : 'outlined'}
                        onClick={() => setStatusFilter(status)}
                    />
                ))}
                {['all', 'false_positive_rule', 'missed_void_clause'].map(type => (
                    <Chip
                        key={type}
                        label={type === 'all' ? 'Alle Typen' : typeLabels[type]}
                        color={typeFilter === type ? 'secondary' : 'default'}
                        variant={typeFilter === type ? 'filled' : 'outlined'}
                        onClick={() => setTypeFilter(type)}
                    />
                ))}
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '360px minmax(0, 1fr)' }, gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {loading && <Typography color="text.secondary">Lade Vorschläge...</Typography>}
                    {!loading && filteredSuggestions.length === 0 && (
                        <Paper elevation={0} sx={{ p: 2 }}>
                            <Typography color="text.secondary">Keine passenden Verbesserungsvorschläge vorhanden.</Typography>
                        </Paper>
                    )}
                    {filteredSuggestions.map(suggestion => (
                        <Paper
                            key={suggestion._id}
                            elevation={0}
                            onClick={() => setSelectedSuggestionId(suggestion._id)}
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                position: 'relative',
                                pb: 4.5,
                                border: selectedSuggestionId === suggestion._id ? '2px solid' : '2px solid transparent',
                                borderColor: selectedSuggestionId === suggestion._id ? 'primary.main' : 'transparent',
                                borderLeft: '6px solid',
                                borderLeftColor: `${getActionColor(suggestion.suggestedAction)}.main`,
                                '&:hover': { bgcolor: 'grey.50' }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                <Chip size="small" label={typeLabels[suggestion.type] || suggestion.type} />
                                <Chip size="small" label={statusLabels[suggestion.status] || suggestion.status} variant="outlined" />
                            </Box>
                            <Typography fontWeight="bold">{getRuleTitle(suggestion.rule)}</Typography>
                            <Typography color="text.secondary" sx={{
                                mt: 0.75,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}>
                                {suggestion.summary}
                            </Typography>
                            <CompactSimulation simulation={suggestion.simulation} />
                        </Paper>
                    ))}
                </Box>

                <Box>
                    {selectedSuggestion && (
                        <SuggestionDetails
                            suggestion={selectedSuggestion}
                            onAccept={() => handleDecision(selectedSuggestion._id, 'accept')}
                            onReject={() => handleDecision(selectedSuggestion._id, 'reject')}
                            canManageSuggestions={canManageSuggestions}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const CompactSimulation = ({ simulation }) => {
    if (!simulation) return null;

    const items = [
        { label: '↑', value: simulation.improvedCount, color: 'success.main' },
        { label: '↓', value: simulation.worsenedCount, color: 'error.main' }
    ];

    return (
        <Box sx={{
            display: 'flex',
            gap: 1.25,
            alignItems: 'center',
            position: 'absolute',
            right: 16,
            bottom: 10
        }}>
            {items.map(item => (
                <Typography key={item.label} sx={{ color: item.color, fontWeight: 700, fontSize: 14, lineHeight: 1 }}>
                    {item.label} {item.value ?? 0}
                </Typography>
            ))}
        </Box>
    );
};

const SuggestionDetails = ({ suggestion, onAccept, onReject, canManageSuggestions }) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper elevation={0} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold">{getRuleTitle(suggestion.rule)}</Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                            <Chip size="small" label={typeLabels[suggestion.type] || suggestion.type} />
                            <Chip
                                size="small"
                                label={actionLabels[suggestion.suggestedAction] || suggestion.suggestedAction}
                                color={getActionColor(suggestion.suggestedAction)}
                            />
                            <Chip size="small" label={statusLabels[suggestion.status] || suggestion.status} variant="outlined" />
                        </Stack>
                    </Box>
                    {suggestion.status === 'open' && (
                        <Stack direction="row" gap={1}>
                            <Tooltip title={canManageSuggestions ? '' : ADMIN_ONLY_ACTION_HINT}>
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CloseIcon />}
                                        onClick={onReject}
                                        disabled={!canManageSuggestions}
                                    >
                                        Ablehnen
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title={canManageSuggestions ? '' : ADMIN_ONLY_ACTION_HINT}>
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<CheckIcon />}
                                        onClick={onAccept}
                                        disabled={!canManageSuggestions}
                                    >
                                        Annehmen
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    )}
                </Box>

                {getNewestVersionText(suggestion.rule) && (
                    <Typography sx={{ whiteSpace: 'pre-wrap', mt: 2 }}>
                        {getNewestVersionText(suggestion.rule)}
                    </Typography>
                )}

                <Box sx={{ mt: 2 }}>
                    <Typography fontWeight="bold">Vorgeschlagene Änderung</Typography>
                    <ProposalList proposals={suggestion.proposals || []} />
                </Box>

                <Divider sx={{ my: 2 }} />
                <SimulationSummary simulation={suggestion.simulation} />

                <Divider sx={{ my: 2 }} />
                <Typography fontWeight="bold">Zusammenfassung</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>{suggestion.summary}</Typography>

                <Typography fontWeight="bold" sx={{ mt: 2 }}>Begründung</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{suggestion.reasoning}</Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight="bold">Evidenz</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    {(suggestion.evidence || []).map((evidence, index) => (
                        <EvidenceItem key={`${evidence.clause?._id || index}-${index}`} suggestion={suggestion} evidence={evidence} />
                    ))}
                </Box>
            </Paper>
        </Box>
    );
};

const SimulationSummary = ({ simulation }) => {
    if (!simulation) {
        return (
            <Typography color="text.secondary">
                Keine Simulationsdaten vorhanden.
            </Typography>
        );
    }

    const items = [
        { label: 'Getestet', value: simulation.totalCount, color: 'default' },
        { label: 'Verbessert', value: simulation.improvedCount, color: 'success' },
        { label: 'Verschlechtert', value: simulation.worsenedCount, color: 'error' },
        { label: 'Unverändert', value: simulation.unchangedCount, color: 'default' }
    ];

    return (
        <Box>
            <Typography fontWeight="bold">Simulation</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                Die vorgeschlagenen Änderungen wurden serverseitig auf die Evidenz-Klauseln angewandt und erneut mit der KI bewertet.
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
                {items.map(item => (
                    <Chip
                        key={item.label}
                        label={`${item.label}: ${item.value ?? 0}`}
                        color={item.color}
                        variant={item.color === 'default' ? 'outlined' : 'filled'}
                    />
                ))}
            </Stack>
        </Box>
    );
};

const ProposalList = ({ proposals }) => {
    const [diffEnabled, setDiffEnabled] = useState({});

    if (proposals.length === 0) {
        return <Typography color="text.secondary" sx={{ mt: 1 }}>Keine konkrete Änderung vorhanden.</Typography>;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {proposals.map((proposal, index) => (
                <Box
                    key={`${proposal.action}-${index}`}
                    sx={{
                        border: '1px solid',
                        borderColor: `${getActionColor(proposal.action)}.main`,
                        borderRadius: 1,
                        p: 2,
                        position: 'relative'
                    }}
                >
                    <Typography fontWeight="bold">{getProposalTitle(proposal)}</Typography>
                    <ProposalTextDiff proposal={proposal} showDiff={!!diffEnabled[index]} />
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        mt: 1
                    }}>
                        {proposal.references?.length > 0 ? (
                            <Typography color="text.secondary">
                                Referenzen: {proposal.references.join(', ')}
                            </Typography>
                        ) : <Box />}
                        {proposal.action === 'update' && getNewestVersionText(proposal.rule) && proposal.text && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={!!diffEnabled[index]}
                                        onChange={(event) => {
                                            setDiffEnabled(prev => ({
                                                ...prev,
                                                [index]: event.target.checked
                                            }));
                                        }}
                                    />
                                }
                                label="Diff"
                                labelPlacement="start"
                                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                            />
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

const ProposalTextDiff = ({ proposal, showDiff }) => {
    const currentText = getNewestVersionText(proposal.rule);

    if (proposal.action === 'delete') {
        return (
            <Typography sx={{ whiteSpace: 'pre-wrap', mt: 1, color: 'error.main', textDecoration: 'line-through' }}>
                {currentText || 'Diese Prüfanleitung wird gelöscht.'}
            </Typography>
        );
    }

    if (proposal.action === 'create') {
        return (
            <Typography sx={{ whiteSpace: 'pre-wrap', mt: 1, color: 'success.main' }}>
                {proposal.text}
            </Typography>
        );
    }

    if (proposal.action === 'update' && currentText && proposal.text && showDiff) {
        const diff = diffWordsWithSpace(currentText, proposal.text);

        return (
            <Typography component="div" sx={{ whiteSpace: 'pre-wrap', mt: 1, lineHeight: 1.7 }}>
                {diff.map((part, index) => (
                    <Box
                        key={`${part.value}-${index}`}
                        component="span"
                        sx={{
                            color: part.added ? 'success.main' : part.removed ? 'error.main' : 'text.primary',
                            bgcolor: part.added ? '#e8f5e9' : part.removed ? '#ffebee' : 'transparent',
                            textDecoration: part.removed ? 'line-through' : 'none'
                        }}
                    >
                        {part.value}
                    </Box>
                ))}
            </Typography>
        );
    }

    if (!proposal.text) return null;

    return (
        <Typography sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
            {proposal.text}
        </Typography>
    );
};

const EvidenceItem = ({ suggestion, evidence }) => {
    const evidenceType = getEvidenceType(suggestion, evidence);
    const evidenceColor = evidenceTypeColors[evidenceType];

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: getChipBorderColor(evidenceColor),
                borderRadius: 1,
                borderLeft: '6px solid',
                borderLeftColor: getChipBorderColor(evidenceColor),
                p: 2
            }}
        >
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Chip
                    size="small"
                    label={evidenceTypeLabels[evidenceType]}
                    color={evidenceColor}
                />
                <Chip
                    size="small"
                    label={outcomeLabels[evidence.outcome] || evidence.outcome}
                    variant="outlined"
                />
            </Stack>
            <Typography color="text.secondary" sx={{ mb: 1 }}>
                {evidenceTypeDescriptions[evidenceType]}
            </Typography>
            <Typography fontWeight="bold">Klausel</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{evidence.clause?.content || 'Keine Klausel geladen.'}</Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
                <ReviewExplanation
                    title={`KI-Bewertung: ${getAssessmentLabel(evidence.aiReview?.assessment)}`}
                    explanation={evidence.aiReview?.explanation || 'Keine Begründung vorhanden.'}
                />
                <ReviewExplanation
                    title={`Anwaltsbewertung: ${getAssessmentLabel(evidence.humanReview?.assessment)}`}
                    explanation={evidence.humanReview?.explanation || 'Keine Anwaltsbewertung vorhanden.'}
                />
            </Box>
            {evidence.simulationOutcome !== 'error' && evidence.simulatedAssessment && (
                <Box sx={{ mt: 2 }}>
                    <SimulationEvidence evidence={evidence} />
                </Box>
            )}
        </Box>
    );
};

const SimulationEvidence = ({ evidence }) => {
    return (
        <Accordion
            disableGutters
            elevation={0}
            sx={{
                border: '1px solid',
                borderColor: getChipBorderColor(getSimulationColor(evidence.simulationOutcome)),
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography fontWeight="bold">
                        Simulation: {getAssessmentLabel(evidence.simulatedAssessment)}
                    </Typography>
                    <Chip
                        size="small"
                        label={simulationOutcomeLabels[evidence.simulationOutcome] || evidence.simulationOutcome}
                        color={getSimulationColor(evidence.simulationOutcome)}
                        variant={getSimulationColor(evidence.simulationOutcome) === 'default' ? 'outlined' : 'filled'}
                    />
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                <Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {evidence.simulatedExplanation || 'Keine Simulationsbegründung vorhanden.'}
                </Typography>
                {evidence.simulatedHurtRules?.length > 0 && (
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        Simulierte verletzte Prüfanleitungen: {evidence.simulatedHurtRules.map(rule => rule?.title || rule?._id || rule).join(', ')}
                    </Typography>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

const ReviewExplanation = ({ title, explanation }) => (
    <Accordion
        disableGutters
        elevation={0}
        sx={{
            border: '1px solid',
            borderColor: 'grey.200',
            '&:before': { display: 'none' }
        }}
    >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
            <Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {explanation}
            </Typography>
        </AccordionDetails>
    </Accordion>
);

export default RuleImprovementSuggestionsTab;
